// Physics — gravity + position integration + brush collision
// Implements Source-style ground/air split:
//   Grounded: slideMove + stepSlideMove (if blocked) + stayOnGround
//   Airborne: slideMove only (surf, falling, jumping)

import { FP, type Fixed } from "./fixed.js";
import { type Vec3Store, type System, BitSet, query } from "./ecs.js";
import { slideMove, stepSlideMove, stayOnGround, sweepAllBrushes, type Brush } from "./collision.js";

export type PhysicsConfig = {
  gravity: Fixed;
  brushes: Brush[];
  halfWidth: Fixed;
  halfHeight: Fixed;
  halfDepth: Fixed;
};

export const DEFAULT_PHYSICS: PhysicsConfig = {
  gravity: FP.from(20),
  brushes: [],
  halfWidth: FP.from(0.4),
  halfHeight: FP.from(0.9),
  halfDepth: FP.from(0.4),
};

const TICK_DT = FP.div(FP.ONE, FP.from(60));
const STEP_SIZE = FP.from(0.5);

/** Per-entity ground material, written by physics each tick. */
export const groundMaterial = new Uint8Array(4096);

export function createPhysicsSystem(
  position: Vec3Store,
  velocity: Vec3Store,
  config: Partial<PhysicsConfig> = {},
  grounded?: BitSet,
): System {
  const cfg = { ...DEFAULT_PHYSICS, ...config };
  const HALF_GRAVITY_DT = FP.div(FP.mul(cfg.gravity, TICK_DT), FP.from(2));

  return () => {
    const { ids, count } = query(position.mask, velocity.mask);

    for (let i = 0; i < count; i++) {
      const id = ids[i];
      const wasGrounded = grounded ? grounded.has(id) : false;

      // StartGravity — half gravity before movement
      velocity.y[id] = FP.sub(velocity.y[id], HALF_GRAVITY_DT);

      if (cfg.brushes.length > 0) {
        // If player jumped this tick (vy > 0 while grounded), switch to air path
        const jumped = wasGrounded && velocity.y[id] > 0;
        if (jumped && grounded) grounded.clear(id);

        if (wasGrounded && !jumped) {
          // --- Grounded path (WalkMove) ---
          velocity.y[id] = FP.ZERO;

          // Try simple trace first (fast path — no step needed if unblocked)
          const destX = FP.add(position.x[id], FP.mul(velocity.x[id], TICK_DT));
          const destZ = FP.add(position.z[id], FP.mul(velocity.z[id], TICK_DT));
          const simpleTrace = sweepAllBrushes(
            position.x[id], position.y[id], position.z[id],
            destX, position.y[id], destZ,
            cfg.halfWidth, cfg.halfHeight, cfg.halfDepth,
            cfg.brushes,
          );

          if (!simpleTrace.hit || simpleTrace.fraction >= FP.ONE) {
            // Unblocked — just move horizontally
            position.x[id] = destX;
            position.z[id] = destZ;
          } else {
            // Blocked — run full stepSlideMove
            const result = stepSlideMove(
              position.x[id], position.y[id], position.z[id],
              velocity.x[id], velocity.y[id], velocity.z[id],
              cfg.halfWidth, cfg.halfHeight, cfg.halfDepth,
              cfg.brushes, TICK_DT, true,
            );
            position.x[id] = result.px;
            position.y[id] = result.py;
            position.z[id] = result.pz;
            velocity.x[id] = result.vx;
            velocity.y[id] = result.vy;
            velocity.z[id] = result.vz;
          }

          // StayOnGround — snap to surface below (makes curved surfaces work)
          const ground = stayOnGround(
            position.x[id], position.y[id], position.z[id],
            cfg.halfWidth, cfg.halfHeight, cfg.halfDepth,
            cfg.brushes, STEP_SIZE,
          );
          position.y[id] = ground.py;
          groundMaterial[id] = ground.material;

          if (grounded) {
            if (ground.onGround) grounded.set(id);
            else grounded.clear(id);
          }
        } else {
          // --- Airborne path (AirMove) ---
          // Regular slideMove — no step-up
          const result = slideMove(
            position.x[id], position.y[id], position.z[id],
            velocity.x[id], velocity.y[id], velocity.z[id],
            cfg.halfWidth, cfg.halfHeight, cfg.halfDepth,
            cfg.brushes, TICK_DT,
          );
          position.x[id] = result.px;
          position.y[id] = result.py;
          position.z[id] = result.pz;
          velocity.x[id] = result.vx;
          velocity.y[id] = result.vy;
          velocity.z[id] = result.vz;

          if (result.onGround) {
            // Landed — snap to ground for stable contact (prevents grounded flicker)
            const ground = stayOnGround(
              position.x[id], position.y[id], position.z[id],
              cfg.halfWidth, cfg.halfHeight, cfg.halfDepth,
              cfg.brushes, STEP_SIZE,
            );
            position.y[id] = ground.py;
            groundMaterial[id] = ground.material;
            if (grounded) grounded.set(id);
          } else {
            groundMaterial[id] = 0;
            if (grounded) grounded.clear(id);
          }
        }
      } else {
        position.x[id] = FP.add(position.x[id], FP.mul(velocity.x[id], TICK_DT));
        position.y[id] = FP.add(position.y[id], FP.mul(velocity.y[id], TICK_DT));
        position.z[id] = FP.add(position.z[id], FP.mul(velocity.z[id], TICK_DT));
      }

      // FinishGravity — other half after movement
      velocity.y[id] = FP.sub(velocity.y[id], HALF_GRAVITY_DT);
    }
  };
}
